import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { termFromDb, termToDb, taskFromDb, medicationFromDb, classFromDb } from '@/lib/mappers'
import { useTasks } from './useTasks'
import { useMedications } from './useMedications'
import { useClasses } from './useClasses'
import { useSchedules } from './useSchedules'

export function useSupabaseData(userId) {
  const [terms, setTerms] = useState([])
  const [activeTermId, setActiveTermId] = useState(null)
  const [displayName, setDisplayName] = useState('')
  const [defaultDayStart, setDefaultDayStart] = useState('08:00')
  const [defaultDayEnd, setDefaultDayEnd] = useState('17:00')
  const [dataLoading, setDataLoading] = useState(true)

  const taskOps = useTasks(activeTermId, userId)
  const medOps = useMedications(activeTermId, userId)
  const classOps = useClasses(activeTermId, userId)
  const schedOps = useSchedules(userId)

  // --- Load terms on mount ---
  async function loadTerms() {
    setDataLoading(true)
    const { data: termsData } = await supabase
      .from('terms')
      .select('*')
      .eq('user_id', userId)
      .order('created_at')

    const { data: prefs } = await supabase
      .from('user_preferences')
      .select('active_term_id, display_name, default_day_start, default_day_end')
      .eq('user_id', userId)
      .single()

    const mappedTerms = (termsData || []).map(termFromDb)
    setTerms(mappedTerms)

    setDisplayName(prefs?.display_name || '')
    setDefaultDayStart(prefs?.default_day_start || '08:00')
    setDefaultDayEnd(prefs?.default_day_end || '17:00')
    const savedActiveId = prefs?.active_term_id
    const activeId = mappedTerms.find(t => t.id === savedActiveId)
      ? savedActiveId
      : mappedTerms[0]?.id || null

    setActiveTermId(activeId)
    setDataLoading(false)
  }

  useEffect(() => {
    if (!userId) return
    loadTerms()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTerms is intentionally excluded; it closes over userId which is already in deps
  }, [userId])

  // --- Load per-term data when activeTermId changes ---
  async function loadTermData(termId) {
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    const [tasksRes, medsRes, medLogRes, classesRes, schedRes] = await Promise.all([
      supabase.from('tasks').select('*').eq('term_id', termId),
      supabase.from('medications').select('*').eq('term_id', termId),
      supabase.from('med_logs').select('log').eq('term_id', termId).single(),
      supabase.from('classes').select('*').eq('term_id', termId),
      supabase.from('daily_schedules').select('*').eq('user_id', userId).eq('schedule_date', todayStr).single(),
    ])

    taskOps._setTasksRaw((tasksRes.data || []).map(taskFromDb))
    medOps._setMedicationsRaw((medsRes.data || []).map(medicationFromDb))
    medOps._setMedLogRaw(medLogRes.data?.log || {})
    classOps._setClassesRaw((classesRes.data || []).map(classFromDb))
    schedOps.loadScheduleFromData(schedRes.data || null)
  }

  useEffect(() => {
    if (!activeTermId || !userId) {
      taskOps._setTasksRaw([])
      medOps.clearMedications()
      classOps.clearClasses()
      return
    }
    loadTermData(activeTermId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTermData and ops objects are intentionally excluded; adding them would cause infinite loops since they're not memoized
  }, [activeTermId, userId])

  async function refresh() {
    if (activeTermId) {
      await loadTermData(activeTermId)
    }
  }

  // --- Term CRUD ---
  async function addTerm(term) {
    setTerms(prev => [...prev, term])
    const { error } = await supabase.from('terms').insert(termToDb(term, userId))
    if (error) {
      console.error('Failed to add term:', error)
      setTerms(prev => prev.filter(t => t.id !== term.id))
    }
  }

  async function updateTerm(termId, updates) {
    setTerms(prev => prev.map(t => t.id === termId ? { ...t, ...updates } : t))
    const dbUpdates = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate
    if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate
    const { error } = await supabase.from('terms').update(dbUpdates).eq('id', termId)
    if (error) console.error('Failed to update term:', error)
  }

  async function deleteTerm(termId) {
    setTerms(prev => prev.filter(t => t.id !== termId))
    const { error } = await supabase.from('terms').delete().eq('id', termId)
    if (error) console.error('Failed to delete term:', error)
  }

  async function switchTerm(termId) {
    setActiveTermId(termId)
    await supabase.from('user_preferences').upsert({
      user_id: userId,
      active_term_id: termId,
    })
  }

  async function updateDisplayName(name) {
    setDisplayName(name)
    await supabase.from('user_preferences').upsert({
      user_id: userId,
      display_name: name,
    })
  }

  // --- Day defaults ---
  async function updateDayDefaults(dayStart, dayEnd) {
    setDefaultDayStart(dayStart)
    setDefaultDayEnd(dayEnd)
    await supabase.from('user_preferences').upsert({
      user_id: userId,
      default_day_start: dayStart,
      default_day_end: dayEnd,
    })
  }

  // Wrap saveSchedule to inject activeTermId (callers don't pass it)
  async function saveSchedule(scheduleDate, dayStart, dayEnd, scheduleItems) {
    return schedOps.saveSchedule(scheduleDate, dayStart, dayEnd, scheduleItems, activeTermId)
  }

  return {
    terms,
    activeTermId,
    tasks: taskOps.tasks,
    medications: medOps.medications,
    medLog: medOps.medLog,
    classes: classOps.classes,
    displayName,
    dataLoading,

    addTerm,
    updateTerm,
    deleteTerm,
    switchTerm,

    setTasks: taskOps.setTasks,
    toggleSubtask: taskOps.toggleSubtask,
    deleteTask: taskOps.deleteTask,
    updateTask: taskOps.updateTask,
    updateSubtask: taskOps.updateSubtask,
    addSubtask: taskOps.addSubtask,
    deleteSubtask: taskOps.deleteSubtask,
    reorderSubtasks: taskOps.reorderSubtasks,
    replaceSubtasks: taskOps.replaceSubtasks,

    addMedication: medOps.addMedication,
    updateMedication: medOps.updateMedication,
    deleteMedication: medOps.deleteMedication,
    toggleMedLog: medOps.toggleMedLog,

    addClass: classOps.addClass,
    updateClass: classOps.updateClass,
    deleteClass: classOps.deleteClass,

    updateDisplayName,

    defaultDayStart,
    defaultDayEnd,
    updateDayDefaults,
    savedSchedule: schedOps.savedSchedule,
    saveSchedule,
    loadScheduleByDate: schedOps.loadScheduleByDate,
    listScheduleDates: schedOps.listScheduleDates,
    refresh,
  }
}
